import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { Box, Group, Text, Button, Skeleton, Select } from '@mantine/core'
import agent from '../../../app/api/agent'
import { Post } from '../../../app/models/post'
import { TagFilterChips, PostCard, EmptyState } from '../../../app/common/ui'
import { useStore } from '../../../app/stores/store'
import { canCreateCommunityPost, MIN_AMMO_SCORE_TO_CREATE_POST } from '../../../app/common/constants/reputation'
import { toast } from 'react-toastify'
import { TagCategory } from '../../../app/models/tag'

const POST_TYPES = [
  { value: undefined as number | undefined, label: 'All' },
  { value: 0, label: 'Blog' },
  { value: 1, label: 'Review' },
  { value: 2, label: 'Ownership Tip' },
  { value: 3, label: 'Discussion' }
]

const CHIP_BASE = {
  background: '#21262D',
  border: '1.5px solid #484F58',
  color: '#E6EDF3',
  borderRadius: 20,
  padding: '4px 12px',
  fontSize: 11,
  fontFamily: 'DM Sans, sans-serif',
  cursor: 'pointer' as const
}

export default observer(function BlogDashboard () {
  const { userStore } = useStore()
  const history = useHistory()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [selectedType, setSelectedType] = useState<number | undefined>(undefined)
  const [sortBy, setSortBy] = useState<string>('latest')

  const pageSize = 20
  const hasMore = posts.length < totalCount

  const fetchPage = async (page: number, append: boolean) => {
    if (page === 1) setLoading(true)
    else setLoadingMore(true)
    try {
      const result = await agent.Posts.list({
        tagIds: selectedTagIds.length ? selectedTagIds : undefined,
        tagMatch: 'any',
        type: selectedType,
        pageNumber: page,
        pageSize,
        sortBy
      })
      setTotalCount(result.totalCount)
      setPosts((prev) => (append ? [...prev, ...result.items] : result.items))
    } catch {
      setPosts((prev) => (append ? prev : []))
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  useEffect(() => {
    setPageNumber(1)
    fetchPage(1, false)
  }, [selectedTagIds, selectedType, sortBy])

  const handleLoadMore = () => {
    const next = pageNumber + 1
    setPageNumber(next)
    fetchPage(next, true)
  }

  const handleTagToggle = (tagId: string) => {
    const next = selectedTagIds.includes(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    setSelectedTagIds(next)
  }

  const mapPostToCard = (p: Post) => (
    <PostCard
      key={p.id}
      id={p.id}
      title={p.title}
      excerpt={p.excerpt}
      type={p.type}
      coverImageUrl={p.coverImageUrl}
      mainPhotoUrl={p.mainPhotoUrl}
      authorUsername={p.authorUsername}
      authorDisplayName={p.authorDisplayName}
      authorImage={p.authorImage}
      authorTier={p.authorAmmoTier}
      authorScore={p.authorAmmoScore}
      voteScore={p.voteScore ?? 0}
      currentUserVote={p.currentUserVote}
      commentCount={p.comments?.length ?? 0}
      createdAt={p.createdAt}
      tags={p.tags}
      rating={p.rating}
      variant="default"
    />
  )

  return (
    <Box sx={{ padding: '16px 24px', maxWidth: 960, margin: '0 auto' }}>
      <Group position="apart" mb="md">
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 20 }}>
          BLOG
        </Text>
        {userStore.isLoggedIn && canCreateCommunityPost(userStore.user) && (
          <Button component={Link} to="/createPost" color="ember" size="sm">
            + New Post
          </Button>
        )}
      </Group>

      <TagFilterChips
        selectedTagIds={selectedTagIds}
        onToggleTag={handleTagToggle}
        categories={[TagCategory.ContentTopic, TagCategory.ReviewSentiment, TagCategory.Platform]}
      />

      <Group spacing="xs" mb="md">
        {POST_TYPES.map(({ value, label }) => {
          const active = selectedType === value
          return (
            <Box
              key={label}
              component="button"
              type="button"
              onClick={() => setSelectedType(value)}
              sx={{
                ...CHIP_BASE,
                ...(active ? { borderColor: '#E85D04', color: '#E85D04', background: 'rgba(232,93,4,0.1)' } : {})
              }}
            >
              {label}
            </Box>
          )
        })}
      </Group>

      <Box mb="md">
        <Select
          label="Sort by"
          placeholder="Select sort order"
          value={sortBy}
          onChange={(val) => setSortBy(val || 'latest')}
          data={[
            { value: 'latest', label: 'Latest first' },
            { value: 'oldest', label: 'Oldest first' },
            { value: 'popular', label: 'Highest voted' }
          ]}
          sx={{
            maxWidth: 200,
            '& .mantine-Select-input': {
              background: '#21262D',
              borderColor: '#484F58',
              color: '#E6EDF3'
            },
            '& .mantine-Select-label': {
              color: '#E6EDF3',
              fontSize: 14,
              fontWeight: 500
            }
          }}
        />
      </Box>

      {loading
        ? (
        <>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={80} radius="sm" mb="sm" />
          ))}
        </>
          )
        : posts.length === 0
          ? (
        <EmptyState
          title="No posts yet"
          message="Be the first to share something"
          action={
            userStore.isLoggedIn
              ? canCreateCommunityPost(userStore.user)
                ? { label: 'Write a post', onClick: () => history.push('/createPost') }
                : {
                    label: `Need ${MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore to post`,
                    onClick: () =>
                      toast.info(
                        `Reach ${MIN_AMMO_SCORE_TO_CREATE_POST} AmmoScore by engaging with the community (votes, comments, contributions) before you can publish posts.`,
                        { autoClose: 6000 }
                      )
                  }
              : { label: 'Register to post', onClick: () => history.push('/register') }
          }
        />
            )
          : (
        <>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {posts.map(mapPostToCard)}
          </Box>
          {hasMore && (
            <Button
              variant="outline"
              mt="md"
              sx={{ borderColor: 'var(--gs-border)', color: 'var(--gs-text)' }}
              onClick={handleLoadMore}
              loading={loadingMore}
            >
              Load more
            </Button>
          )}
        </>
            )}
    </Box>
  )
})
